import { DataReference, DataReferenceCollection, DataImplementation, } from '../data/index.js';
import { FeatureReference, FeatureReferenceCollection, } from '../features/index.js';
const { PatternType } = FeatureReferenceCollection.Query;
/**
 * @function dataReference
 * @memberof adapters.FromSeekAPI
 * @inner
 * @param {SeekDataReference} fromSeek
 * @return {data-references.DataReference}
 */
export function dataReference(fromSeek) {
    return new DataReference({
        type: fromSeek.type,
        column: fromSeek.type === DataReference.SourceType.SYSTEM
            ? parseInt(fromSeek.column, 10)
            : fromSeek.column,
        sourceId: fromSeek.sourceId,
    });
}
/**
 * @function dataReferenceCollection
 * @memberof adapters.FromSeekAPI
 * @inner
 * @param {object} fromSeek
 * @param {?number} fromSeek.id
 * @param {?SeekDataReference[]} fromSeek.variables
 * @param {object} fromSeek.query
 * @return {data-references.DataReferenceCollection}
 */
export function dataReferenceCollection(fromSeek) {
    const collection = new DataReferenceCollection();
    if (fromSeek.variables.length) {
        collection.variables = fromSeek.variables.map(dataReference);
    }
    else {
        collection.query = new DataReferenceCollection.Query({ ...fromSeek.query });
    }
    return collection;
}
/**
 * @function dataImplementation
 * @memberof adapters.FromSeekAPI
 * @inner
 * @param {object} fromSeek
 * @return {data-references.DataImplementation}
 */
export function dataImplementation(fromSeek) {
    const hasNormalization = !!fromSeek.normalization;
    const props = {
        ...fromSeek,
        ref: dataReferenceCollection(fromSeek.ref),
        normalization: new DataImplementation.Normalization({
            ref: (hasNormalization
                ? dataReferenceCollection(fromSeek.normalization)
                : null),
        }),
    };
    const implementation = new DataImplementation(props);
    return implementation;
}
/**
 * @function featureReference
 * @memberof adapters.FromSeekAPI
 * @inner
 * @param {SeekFeatureReference} fromSeek
 * @return {feature-references.FeatureReference}
 */
export function featureReference(fromSeek) {
    const id = parseInt(fromSeek.featureId.toString(), 10);
    const sourceId = parseInt(fromSeek.sourceId.toString(), 10);
    return new FeatureReference({
        id,
        sourceId,
        type: fromSeek.type,
    });
}
/**
 * @function spatialQuery
 * @private
 * @param {PatternType} query.pattern
 * @param {object} query.parameters
 * @param {?data-references.FeatureReference} query.parameters.constrainBy
 * @param {?data-references.LayerReference} query.parameters.layer
 * @return {object}
 */
function spatialQuery(fromSeek) {
    const { constrainBy, layer } = fromSeek.parameters;
    return {
        pattern: fromSeek.pattern,
        parameters: {
            ...fromSeek.parameters,
            layer: {
                type: layer.type,
                sourceId: parseInt(layer.sourceId.toString(), 10),
            },
            constrainBy: (!!constrainBy
                ? new FeatureReference({
                    id: parseInt(constrainBy.id.toString(), 10),
                    type: constrainBy.type,
                    sourceId: parseInt(constrainBy.sourceId.toString(), 10),
                })
                : null),
        }
    };
}
/**
 * @function featureReferenceCollection
 * @memberof adapters.FromSeekAPI
 * @inner
 * @param {object} fromSeek
 * @param {?number} fromSeek.id
 * @param {?SeekFeatureReference[]} fromSeek.features
 * @param {?object} fromSeek.query
 * @return {feature-references.FeatureReferenceCollection}
 */
export function featureReferenceCollection(fromSeek) {
    const collection = new FeatureReferenceCollection();
    if (fromSeek.features.length) {
        collection.features = fromSeek.features.map(featureReference);
    }
    else {
        const QUERY_PATTERN_MAP = {
            [PatternType.SPATIAL_QUERY]: spatialQuery,
        };
        const props = QUERY_PATTERN_MAP[fromSeek.query.pattern](fromSeek.query);
        collection.query = new FeatureReferenceCollection.Query(props);
    }
    return collection;
}
/**
 * @function collections
 * @memberof adapters.FromSeekAPI
 * @inner
 * @param {object} fromSeek
 * @param {?object[]} fromSeek.dataImplementations
 * @param {?object[]} fromSeek.dataReferenceCollections
 * @param {?object[]} fromSeek.featureReferenceCollections
 * @return {object}
 */
export function collections({ dataImplementations, dataReferenceCollections, featureReferenceCollections, }) {
    const features = (featureReferenceCollections
        ? featureReferenceCollections.map(featureReferenceCollection)
        : featureReferenceCollections);
    const dataReferences = (dataReferenceCollections
        ? dataReferenceCollections.map(dataReferenceCollection)
        : dataReferenceCollections);
    const implementations = (dataImplementations
        ? dataImplementations.map(dataImplementation)
        : dataImplementations);
    return {
        featureReferenceCollections: features,
        dataReferenceCollections: dataReferences,
        dataImplementations: implementations,
    };
}
/**
 * @namespace
 * @memberof adapters
 */
const FromSeekAPI = {
    dataReference,
    dataReferenceCollection,
    dataImplementation,
    featureReference,
    featureReferenceCollection,
    collections,
};
export default FromSeekAPI;
